import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Table } from 'antd'
import Chart from 'react-apexcharts'
import { COMPANIES_TRANSLATIONS } from '../../lib/companies'
import ChartTableSwitch from './ChartTableSwitch'


const { Title } = Typography

const COLORS = {
  Medtronic: '#3695dc',
  Abbott: '#5abf3f',
  Boston: '#Fcaa58',
  Biotronik: '#Ff3d4a',
  Sorin: '#AD18F2',
}

function translateCompany(input) {
  return COMPANIES_TRANSLATIONS[input] || input
}

export default function DevicesSummary({ devicesStats }) {
  const [showChart, setShowChart] = useState(true)
  const piesData = useMemo(
    () =>
      devicesStats.map(deviceTypeData => ({
        title: deviceTypeData.name,
        total: deviceTypeData.count,
        chartData: deviceTypeData.manufacturers.map(data => ({
          value: data.count,
          color: COLORS[translateCompany(data.name)],
          title: translateCompany(data.name),
          key: translateCompany(data.name),
        })),
      })),
    [devicesStats],
  )

  const devicesPerManufacturer = devicesStats.reduce((acc, deviceTypeData) => {
    deviceTypeData.manufacturers.forEach(manufacturerData => {
      const companyName = translateCompany(manufacturerData.name)
      if (!acc[companyName]) {
        acc[companyName] = 0
      }
      acc[companyName] += manufacturerData.count
    })
    return acc
  }, {})

  const chartSeries = useMemo(
    () =>
      Object.keys(devicesPerManufacturer)
        .sort()
        .map(company => ({
          name: company,
          data: piesData.map(
            pieData =>
              pieData.chartData.find(d => d.key === company)?.value || 0,
          ),
        })),
    [devicesPerManufacturer, piesData],
  )

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          columnWidth: '30%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: false,
      },
      xaxis: {
        categories: piesData.map(pieData => pieData.title),
      },
      legend: {
        position: 'bottom',
        offsetY: 0,
      },
      colors: Object.keys(devicesPerManufacturer).sort().map(
        company => COLORS[company],
      ),
    }),
    [piesData, devicesPerManufacturer],
  )

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Title level={3}>Devices summary</Title>
        <ChartTableSwitch onChange={value => setShowChart(value === 'chart')} viewMode={showChart ? 'chart' : 'table'} />
      </div>

      {showChart && (
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="bar"
          height={300}
        />
      )}

      {!showChart && (
      <Table
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Manufacturer',
            dataIndex: 'manufacturer',
            key: 'manufacturer',
          },
          {
            title: 'Total',
            dataIndex: 'count',
            key: 'count',
            align: 'center',
          },
          ...devicesStats.map(deviceTypeData => ({
            title: deviceTypeData.name,
            dataIndex: deviceTypeData.name,
            key: deviceTypeData.name,
            align: 'center',
            render: (_, record) => {
              const companyName = record.manufacturer
              const manufacturerData = deviceTypeData.manufacturers.find(
                m => translateCompany(m.name) === companyName,
              )
              return manufacturerData ? manufacturerData.count : 0
            },
          })),
        ]}
        dataSource={Object.keys(devicesPerManufacturer).map(company => ({
          key: company,
          manufacturer: company,
          count: devicesPerManufacturer[company],
          }))
        }
      />
      )}
    </>
  )
}

DevicesSummary.propTypes = {
  devicesStats: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
}
